import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import { LogEntry, WsState, fetchLogHistory } from '../api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface Props {
  logs: LogEntry[]
  getAllLogs?: () => LogEntry[]
  onClear: () => void
  wsState: WsState
  showDebug?: boolean
  // Idea #54: чтобы кнопка «Скачать» брала полный лог из БД для
  // активного проекта вместо обрезанного ring-buffer'а. Если не
  // передано — фолбэк на blob из буфера (как было раньше).
  campaignId?: string
  mode?: 'booster' | 'profiler'
}

type SemanticType =
  | 'success' | 'found' | 'step' | 'action' | 'setup'
  | 'sclick' | 'smartpilot' | 'captcha' | 'proxy'
  | 'warning' | 'error' | 'critical' | 'rate_limit'
  | 'not_found' | 'debug' | 'info'

interface SemColors {
  text: string
  bg: string
  border: string
  badge?: string
  badgeBg?: string
}

interface EnrichedEntry {
  entry: LogEntry
  sem: SemanticType
  parsed: { tag: string; rest: string } | null
}

const LEVELS = ['DEBUG', 'INFO', 'SUCCESS', 'WARNING', 'ERROR'] as const

const LEVEL_BTN_CONFIG: Record<string, { color: string; activeColor: string; label: string }> = {
  DEBUG:   { color: 'text-muted-foreground', activeColor: 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border-zinc-500/30', label: 'Debug' },
  INFO:    { color: 'text-muted-foreground', activeColor: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30', label: 'Info' },
  SUCCESS: { color: 'text-muted-foreground', activeColor: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30', label: 'OK' },
  WARNING: { color: 'text-muted-foreground', activeColor: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30', label: 'Warn' },
  ERROR:   { color: 'text-muted-foreground', activeColor: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30', label: 'Error' },
}

const SEM_COLORS: Record<SemanticType, SemColors> = {
  success:    { text: 'text-green-700 dark:text-green-300',    bg: 'bg-green-50 dark:bg-green-950/40',       border: 'border-l-green-500 dark:border-l-green-400',    badge: 'text-green-700 dark:text-green-300',    badgeBg: 'bg-green-100 dark:bg-green-900/60' },
  found:      { text: 'text-cyan-700 dark:text-cyan-300',      bg: 'bg-cyan-50 dark:bg-cyan-950/25',         border: 'border-l-cyan-500 dark:border-l-cyan-400',      badge: 'text-cyan-700 dark:text-cyan-300',      badgeBg: 'bg-cyan-100 dark:bg-cyan-900/40' },
  step:       { text: 'text-blue-600 dark:text-blue-400',      bg: '',                                       border: 'border-l-blue-400 dark:border-l-blue-600',      badge: 'text-blue-600 dark:text-blue-400',      badgeBg: 'bg-blue-100 dark:bg-blue-900/30' },
  action:     { text: 'text-teal-600 dark:text-teal-300',      bg: 'bg-teal-50/50 dark:bg-teal-950/20',      border: 'border-l-teal-500 dark:border-l-teal-400',      badge: 'text-teal-700 dark:text-teal-200',      badgeBg: 'bg-teal-600 dark:bg-teal-700' },
  setup:      { text: 'text-slate-500 dark:text-slate-400',    bg: '',                                       border: 'border-l-slate-300 dark:border-l-slate-600' },
  sclick:     { text: 'text-indigo-700 dark:text-indigo-300',  bg: 'bg-indigo-50 dark:bg-indigo-950/20',     border: 'border-l-indigo-500 dark:border-l-indigo-400',  badge: 'text-indigo-700 dark:text-indigo-300',  badgeBg: 'bg-indigo-100 dark:bg-indigo-900/40' },
  smartpilot: { text: 'text-violet-700 dark:text-violet-300',  bg: 'bg-violet-50 dark:bg-violet-950/20',     border: 'border-l-violet-500 dark:border-l-violet-400',  badge: 'text-violet-700 dark:text-violet-300',  badgeBg: 'bg-violet-100 dark:bg-violet-900/40' },
  captcha:    { text: 'text-purple-700 dark:text-purple-300',  bg: 'bg-purple-50 dark:bg-purple-950/20',     border: 'border-l-purple-500 dark:border-l-purple-400',  badge: 'text-purple-700 dark:text-purple-300',  badgeBg: 'bg-purple-100 dark:bg-purple-900/40' },
  proxy:      { text: 'text-orange-600 dark:text-orange-400',  bg: '',                                       border: 'border-l-orange-400 dark:border-l-orange-600',  badge: 'text-orange-600 dark:text-orange-400',  badgeBg: 'bg-orange-100 dark:bg-orange-900/30' },
  warning:    { text: 'text-amber-700 dark:text-amber-300',    bg: 'bg-amber-50 dark:bg-amber-950/15',       border: 'border-l-amber-500',                            badge: 'text-amber-700 dark:text-amber-300',    badgeBg: 'bg-amber-100 dark:bg-amber-900/40' },
  // YANDEX 429 / IP rate-limits — distinct rose tone so they don't blend
  // with regular errors. Slightly warmer than 'error', sits between
  // amber and red on the spectrum, matches the perception of "rate
  // limit / cooldown" rather than "broken".
  rate_limit: { text: 'text-rose-700 dark:text-rose-300',      bg: 'bg-rose-50 dark:bg-rose-950/25',         border: 'border-l-rose-500 dark:border-l-rose-400',      badge: 'text-rose-700 dark:text-rose-300',      badgeBg: 'bg-rose-100 dark:bg-rose-900/40' },
  // "Сайт не найден в выдаче" — soft pink. Operator wants this softer
  // than a real error: the worker is going to retry / try the brand,
  // so it's a "soft fail signal", not a hard crash. Pinks live next to
  // rose but slightly cooler.
  not_found:  { text: 'text-pink-700 dark:text-pink-300',      bg: 'bg-pink-50 dark:bg-pink-950/25',         border: 'border-l-pink-500 dark:border-l-pink-400',      badge: 'text-pink-700 dark:text-pink-300',      badgeBg: 'bg-pink-100 dark:bg-pink-900/40' },
  // 'error' is reserved for actual failures (Сценарий FAILED, click
  // failures, etc). Bumped from red-300/700 to red-400/600 for a more
  // saturated red that clearly stands out from the rate-limit rose.
  error:      { text: 'text-red-600 dark:text-red-400',        bg: 'bg-red-50 dark:bg-red-950/30',           border: 'border-l-red-500',                              badge: 'text-red-600 dark:text-red-400',        badgeBg: 'bg-red-100 dark:bg-red-900/50' },
  critical:   { text: 'text-red-700 dark:text-red-300',        bg: 'bg-red-100 dark:bg-red-950/50',          border: 'border-l-red-600 dark:border-l-red-400',        badge: 'text-red-700 dark:text-red-200',        badgeBg: 'bg-red-200 dark:bg-red-900/70' },
  debug:      { text: 'text-zinc-400 dark:text-zinc-500',      bg: '',                                       border: 'border-l-zinc-300 dark:border-l-zinc-700' },
  info:       { text: 'text-zinc-500 dark:text-zinc-400',      bg: '',                                       border: 'border-l-zinc-300 dark:border-l-zinc-600' },
}

const SEM_BADGES: Partial<Record<SemanticType, string>> = {
  success: '✓✓', found: '◎', step: '»', sclick: 'SK', smartpilot: 'AI',
  captcha: '🛡', proxy: '⇄', warning: '⚠', error: '✗', critical: '✗✗',
  rate_limit: '429', not_found: '?',
}

// Служебные сообщения — скрываем полностью (не показываем пользователю)
const HIDDEN_KW = [
  'Chrome env:',
  'Поток остановлен',
  'Поток завершён',
  'Поток запущен (PID=',
  '=== Итерация',
  // Machine telemetry for Captcha tab — never show in operator log.
  'BB_STAT:',
]

// YANDEX_429 / yandex-rate-limit branch is a *first-class* category and
// must win over generic ERROR/CRITICAL classification, so a single
// "Сценарий FAILED | причина=yandex_429" line is still painted in the
// 429 rose hue rather than the regular red.
const RATE_LIMIT_KW = ['YANDEX_429', 'yandex_429', 'Yandex 429', 'YANDEX 429']
// "Сайт не найден в выдаче" — soft fail, painted pink (not red): the
// worker either retries with brand or fails the iteration. Operators
// asked to differentiate it from real errors so it doesn't look like
// the system is broken.
const NOT_FOUND_KW  = ['Сайт не найден в выдаче']
// "Воркер завершен неудачно" — final operator-facing red line that
// replaces the older triple of "Сценарий FAILED / BOOSTER_RESULT:FAIL /
// сессия не сохранена".
const FAIL_FINAL_KW = ['Воркер завершен неудачно']
const SUCCESS_KW    = ['Воркер завершен успешно', 'успешно создан', 'успешно прокачен', '▶ Запуск:']
const FOUND_KW      = ['Найден на позиции', 'Найден в выдаче']
const STEP_KW       = ['Задержка запуска', 'Ghost режим', 'GHOST режим', 'Поиск в Яндексе', 'Переход на выдачу', 'Переход в Яндекс', 'Ищем ', 'Поведение на выдаче', 'Кликаем по целевому', 'Выполняем клик по', 'Перешли на сайт', 'Финальный запрос', 'Страница ', 'Ожидание', 'Хождение по сайту:', '✓ Клик выполнен', '✓ Хождение завершено', 'Переход выполнен', '✓ Страница восстановлена', 'Повторный клик:', 'Выполнен повторный клик', 'Мультисессия:', 'Закрыто']
const SETUP_KW      = ['Выбран профиль', 'Отпечаток', 'BAS-конвертация', 'Генератор отпечатков', 'Браузер с отпечатком', 'Задание:', 'Задание (', 'Региональные куки', 'Куки восстановлены', 'Конфиг восстановлен', 'Файлы профиля восстановлены', 'Регион:', 'Space claimed', 'Galaxy claimed', 'Claimed PID', 'Calibrated:', '✓ Яндекс.Метрика заблокирована', 'PF_TYPE=', 'Получили домен для продвижения', 'Количество заданий в работе', 'Used Galaxy\'s Stats']
const SCLICK_KW     = ['Склик:', 'склик']
const SMARTPILOT_KW = ['SmartPilot']
const CAPTCHA_KW    = ['капч', 'Капча', 'captcha', 'CAPTCHA', '🛡']
const PROXY_KW      = ['Прокси установлено', '✓ Прокси подключен', 'Прокси недоступен', 'прокси', 'SOCKS5']
const CRITICAL_KW   = ['BOOSTER_RESULT:FAIL', 'ошибок подряд', 'Ошибка сценария', 'browser_crash', 'browser_timeout', 'Нет доступных прокси', 'Нет профилей в таблице', 'infra —']
const ERROR_KW      = ['ошибка', 'Ошибка', 'не удалось', 'Не удалось', 'не найден', 'не решена', 'timeout', 'timed out', 'crash', 'FAILED', 'промах', 'Клик не удался', 'не загрузилась']
const WARNING_KW    = ['пропускаем', 'не сохранён', 'не завершён', 'пустые', 'перезагружаем', 'недоступен', 'Лимит', 'повтор', 'НЕ сохранён', 'Пауза потока', 'Таймаут']
const ACTION_RE     = /\[#\d{2}\]/

// Explicit colour marker emitted by the «Лог» scenario cube: ⟦c:NAME⟧ at the
// head of the message. It overrides auto-semantics and is stripped before the
// line is shown. Uses U+27E6/U+27E7 brackets so it never collides with stats
// parsers (PROFILER_RESULT / thread_fail / _S_SUCCESS).
const COLOR_MARKER_TO_SEM: Record<string, SemanticType> = {
  green: 'success', cyan: 'found', blue: 'step', amber: 'warning',
  orange: 'proxy', purple: 'captcha', red: 'error', critical: 'critical',
  rose: 'rate_limit', pink: 'not_found', gray: 'info',
}
const COLOR_MARKER_RE   = /⟦c:([a-z_]+)⟧/
const COLOR_MARKER_RE_G = /⟦c:[a-z_]+⟧/g

// Returns the forced semantic colour if the message carries a colour marker.
function forcedColorSem(message: string): SemanticType | null {
  const m = message.match(COLOR_MARKER_RE)
  if (!m) return null
  return COLOR_MARKER_TO_SEM[m[1]] || null
}

function classifySemantic(level: string, message: string): SemanticType {
  const has = (kw: string[]) => kw.some(k => message.includes(k))
  if (ACTION_RE.test(message)) return 'action'
  // Highest priority: rate-limit (429) wins over level so that both
  // INFO ("YANDEX_429: попытка 1/8") and ERROR ("Сценарий FAILED |
  // причина=yandex_429") share the same rose color.
  if (has(RATE_LIMIT_KW)) return 'rate_limit'
  // "Воркер завершен неудачно" — operator-facing red final line.
  // Must outrank generic ERROR so it gets the saturated red badge
  // even when the message also matches NOT_FOUND_KW (it doesn't,
  // but explicit ordering > implicit order).
  if (has(FAIL_FINAL_KW)) return 'error'
  // "Сайт не найден в выдаче" — soft pink, distinct from hard errors.
  if (has(NOT_FOUND_KW)) return 'not_found'
  if (level === 'CRITICAL') return 'critical'
  if (level === 'ERROR') return has(CAPTCHA_KW) ? 'captcha' : has(CRITICAL_KW) ? 'critical' : 'error'
  if (level === 'SUCCESS' || has(SUCCESS_KW)) return 'success'
  if (has(FOUND_KW)) return 'found'
  if (has(SMARTPILOT_KW)) return 'smartpilot'
  if (has(CAPTCHA_KW)) return 'captcha'
  if (has(SCLICK_KW)) return 'sclick'
  if (has(STEP_KW)) return 'step'
  if (has(SETUP_KW)) return 'setup'
  if (has(PROXY_KW)) return 'proxy'
  if (has(CRITICAL_KW)) return 'critical'
  if (level === 'WARNING') return has(ERROR_KW) ? 'error' : 'warning'
  if (has(ERROR_KW)) return 'error'
  if (has(WARNING_KW)) return 'warning'
  if (level === 'DEBUG') return 'debug'
  return 'info'
}

function fmtTime(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString('ru-RU', { hour12: false })
}

// Split a log message into alternating <text, url> segments so URLs can be
// rendered a touch smaller than the surrounding text (they're usually long
// and noisy; making them slightly smaller makes log rows more scannable).
function renderMessageWithUrls(msg: string, urlClass: string): React.ReactNode {
  if (!msg) return msg
  const re = /https?:\/\/[^\s|)]+/g
  const parts: React.ReactNode[] = []
  let lastIdx = 0
  let m: RegExpExecArray | null
  let key = 0
  while ((m = re.exec(msg)) !== null) {
    if (m.index > lastIdx) parts.push(msg.slice(lastIdx, m.index))
    parts.push(
      <span key={`u${key++}`} className={urlClass}>{m[0]}</span>,
    )
    lastIdx = m.index + m[0].length
  }
  if (lastIdx === 0) return msg          // no URLs — plain string
  if (lastIdx < msg.length) parts.push(msg.slice(lastIdx))
  return parts
}


// Strip noisy technical details from CAPTCHA/PoW logs (all platforms).
function stripPowDetails(msg: string): string {
  // Remove `| pdata={...}` and `| pdata(начало)={...}` blobs entirely.
  msg = msg.replace(/\s*\|\s*pdata(?:\([^)]*\))?=\{[^}]*\}?/gi, '')
  // Remove stray `| pdata=...` up to next `|` or end of string
  msg = msg.replace(/\s*\|\s*pdata(?:\([^)]*\))?=[^|]*(?=\s*\||$)/gi, '')
  // Collapse leftover "|  |"
  msg = msg.replace(/\s*\|\s*\|\s*/g, ' | ')
  return msg.trim()
}

// Compact long technical strings for mobile readability (URLs, IPv6, tcp-fingerprints, long hex).
function shortenForMobile(msg: string): string {
  // Replace full URLs with "host…"
  msg = msg.replace(/https?:\/\/[^\s|)]+/g, (url) => {
    try {
      const u = new URL(url)
      const path = u.pathname.length > 1 || u.search ? '/…' : ''
      return u.hostname + path
    } catch {
      return url.length > 24 ? url.slice(0, 20) + '…' : url
    }
  })
  // Collapse IPv6 addresses (6+ groups)
  msg = msg.replace(/\b[0-9a-f]{1,4}(?::[0-9a-f]{1,4}){5,}\b/gi, 'IPv6')
  // TCP p0f-style fingerprints: "6:58+6:0:1429:65125,13:mss,sok,ts,..." → "tcp-fp"
  msg = msg.replace(/\b\d+:\d+\+\d+:\d+:\d+:\d+,\d+:[a-z,:]+/gi, 'tcp-fp')
  // Generic long hex blobs (32+ chars)
  msg = msg.replace(/\b[0-9a-f]{32,}\b/gi, m => m.slice(0, 8) + '…')
  // Repeating "key: very_long_value" chunks with pipes — dedupe whitespace
  msg = msg.replace(/\s+\|\s+/g, ' | ')
  return msg
}

function parseMessage(msg: string): { tag: string; rest: string } | null {
  const m = msg.match(/^\[W(\d+)\]\s*/)
  return m ? { tag: `W${m[1]}`, rest: msg.slice(m[0].length) } : null
}

// Extract leading [Project name] prefix from message body.
function splitProjectPrefix(rest: string): { project: string; body: string } {
  const m = rest.match(/^\[([^\]]+)\]\s*/)
  if (m) return { project: m[1], body: rest.slice(m[0].length) }
  return { project: '', body: rest }
}

const WS_LABELS: Record<string, { text: string; cls: string }> = {
  connecting:   { text: 'Подключение...', cls: 'text-amber-500' },
  connected:    { text: '', cls: 'text-emerald-500' },
  disconnected: { text: 'Переподключение...', cls: 'text-red-400' },
}

// «Прилипание» к низу: если оператор в пределах STICK_THRESHOLD px от низа —
// считаем что он «у нижнего края» и авто-скролл включён. Стоит ровно столько
// же, сколько передаём в <Virtuoso atBottomThreshold>, чтобы геометрический
// расчёт в onScroll и внутренняя детекция Virtuoso совпадали.
const STICK_THRESHOLD = 40

// Live "load older" (scroll-up history from logs.db) tuning.
const HISTORY_PAGE = 500          // lines pulled per scroll-up request
const MAX_OLDER = 20000           // soft cap on locally-held history (~10 MB)
// Large base index for react-virtuoso prepend: decremented by the number of
// rendered older lines we prepend, so the viewport doesn't jump on load.
const VIRTUOSO_START = 1_000_000

export default function LogPanel({ logs, getAllLogs, onClear, wsState, showDebug = false, campaignId, mode = 'booster' }: Props) {
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  // autoScroll === «прилипание включено» (оператор у нижнего края лога).
  const [autoScroll, setAutoScroll] = useState(true)
  const autoRef = useRef(true)
  const lastScrollTopRef = useRef(0)
  // Бейдж «N новых логов» — копится пока прилипание выключено и приходят строки.
  const [newCount, setNewCount] = useState(0)
  const prevLenRef = useRef(0)
  const [workerFilter, setWorkerFilter] = useState('0')
  const [activeLevels, setActiveLevels] = useState<Set<string>>(() => new Set(['INFO', 'SUCCESS', 'WARNING', 'ERROR']))
  const [search, setSearch] = useState('')
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  // ── Live "load older" history (scroll-up) ──
  // `older` holds historical lines pulled from logs.db on demand; the live
  // WS buffer (props.logs) stays "hot". Combined for rendering: [older, live].
  const [older, setOlder] = useState<LogEntry[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [firstItemIndex, setFirstItemIndex] = useState(VIRTUOSO_START)
  const cursorRef = useRef<number | null>(null)        // smallest loaded id → next before_id
  const hasMoreRef = useRef(false)
  const loadingOlderRef = useRef(false)
  const olderRef = useRef<LogEntry[]>([])
  const logsRef = useRef<LogEntry[]>(logs)
  const readyRef = useRef(false)                        // gate auto-load until first settle
  useEffect(() => { olderRef.current = older }, [older])

  // Живой буфер в App.tsx ограничен MAX_LIVE_LOGS и при переполнении
  // ВЫТЕСНЯЕТ старые строки с НАЧАЛА (`slice(-MAX)`). Для Virtuoso это сдвиг
  // индексов: строка, на которую смотрит оператор, меняет свой индекс, и
  // список «уезжает» вперёд даже с выключенным прилипанием. Лечится тем же
  // механизмом, что и подгрузка истории вверх: `firstItemIndex` надо СДВИНУТЬ
  // на столько отрендеренных строк, сколько вытеснено с начала (при prepend мы
  // его уменьшаем, при вытеснении — увеличиваем). Без этого при 100 потоках
  // буфер прокручивается целиком за минуту-две и читать лог выше было нельзя.
  useEffect(() => {
    const prev = logsRef.current
    logsRef.current = logs
    if (prev === logs || prev.length === 0 || logs.length === 0) return
    const key = (e: LogEntry) => e.time + '|' + e.message
    const headKey = key(logs[0])
    if (key(prev[0]) === headKey) return          // начало не менялось — вытеснения нет
    const dropped = prev.findIndex(e => key(e) === headKey)
    if (dropped <= 0) return                      // не нашли якорь — не угадываем
    const renderedDropped = prev.slice(0, dropped).filter(passesFilterRef.current).length
    if (renderedDropped > 0) setFirstItemIndex(i => i + renderedDropped)
  }, [logs])

  const setAutoScrollSync = useCallback((val: boolean) => {
    autoRef.current = val
    setAutoScroll(val)
  }, [])

  // Clearing the live log also drops the loaded history + resets the cursor.
  const handleClear = useCallback(() => {
    setOlder([]); olderRef.current = []
    cursorRef.current = null
    const enable = !!campaignId
    hasMoreRef.current = enable
    setHasMore(enable)
    setFirstItemIndex(VIRTUOSO_START)
    onClear()
  }, [onClear, campaignId])

  const parsedWorker = useMemo(() => {
    const n = parseInt(workerFilter, 10)
    return isNaN(n) || n <= 0 ? null : n - 1
  }, [workerFilter])

  const toggleLevel = useCallback((lv: string) => {
    setActiveLevels(prev => {
      const next = new Set(prev)
      if (next.has(lv)) next.delete(lv); else next.add(lv)
      return next
    })
  }, [])

  // Predicate shared by the live + older memos AND by loadOlder (so the
  // firstItemIndex decrement matches exactly how many older lines render).
  const passesFilter = useCallback((l: LogEntry): boolean => {
    if (HIDDEN_KW.some(kw => l.message.includes(kw))) return false
    if (parsedWorker !== null && l.worker_id !== parsedWorker) return false
    if (!activeLevels.has(l.level || 'INFO')) return false
    const q = search.toLowerCase()
    if (q && !l.message.toLowerCase().includes(q)) return false
    return true
  }, [parsedWorker, activeLevels, search])

  const passesFilterRef = useRef(passesFilter)
  useEffect(() => { passesFilterRef.current = passesFilter }, [passesFilter])

  const enrich = useCallback((l: LogEntry): EnrichedEntry => ({
    entry: l,
    // Explicit cube colour marker wins over auto-semantics.
    sem: forcedColorSem(l.message) ?? classifySemantic(l.level, l.message),
    parsed: parseMessage(l.message),
  }), [])

  // Live portion (props.logs) drives the "N new" badge + autoscroll. Older
  // portion is the lazily-loaded history rendered above the live lines.
  const liveFiltered = useMemo<EnrichedEntry[]>(
    () => logs.filter(passesFilter).map(enrich),
    [logs, passesFilter, enrich])
  const olderFiltered = useMemo<EnrichedEntry[]>(
    () => older.filter(passesFilter).map(enrich),
    [older, passesFilter, enrich])
  const filtered = useMemo<EnrichedEntry[]>(
    () => olderFiltered.length ? [...olderFiltered, ...liveFiltered] : liveFiltered,
    [olderFiltered, liveFiltered])

  // Копим счётчик новых строк только когда прилипание выключено (оператор
  // читает выше). Считаем по ЖИВОЙ части — подгрузка истории вверх не должна
  // считаться «новыми» строками.
  useEffect(() => {
    const len = liveFiltered.length
    const prev = prevLenRef.current
    prevLenRef.current = len
    if (len < prev) {
      // Лог очищен / ужат фильтром — сбрасываем бейдж.
      setNewCount(0)
    } else if (len > prev && !autoRef.current) {
      setNewCount(c => c + (len - prev))
    }
  }, [liveFiltered.length])

  // Сами «приклеиваем» лог к низу, когда прилипание включено. НЕ используем
  // <Virtuoso followOutput>, потому что у followOutput есть встроенный
  // «size-increased» backstop: когда список РАСТЁТ (а логи растят его каждые
  // ~0.3с), Virtuoso насильно прыгает вниз ДАЖЕ если оператор отскроллил вверх
  // — backstop проверяет лишь то, что followOutput !== false (а не результат
  // нашей функции и не реальную позицию). Именно поэтому лог «убегал вниз»
  // после ручного скролла. Теперь followOutput жёстко выключен (false), а
  // авто-скролл делаем здесь руками и ТОЛЬКО когда autoRef.current === true.
  // Программный скролл всегда идёт ВНИЗ (увеличивает scrollTop), поэтому он не
  // путается с ручным скроллом вверх в onScroll-детекторе ниже.
  useEffect(() => {
    if (!autoRef.current || filtered.length === 0) return
    virtuosoRef.current?.scrollToIndex({ index: filtered.length - 1, align: 'end', behavior: 'auto' })
  }, [filtered.length])

  // Pull a page of OLDER lines for this project's current/last run from
  // logs.db and prepend them. The firstItemIndex decrement (by how many
  // prepended lines actually render under the current filter) keeps the
  // viewport anchored — no jump. Cursor decreases monotonically → terminates.
  const loadOlder = useCallback(async () => {
    if (!campaignId || loadingOlderRef.current || !hasMoreRef.current) return
    if (olderRef.current.length >= MAX_OLDER) { hasMoreRef.current = false; setHasMore(false); return }
    loadingOlderRef.current = true
    setLoadingOlder(true)
    try {
      const res = await fetchLogHistory(mode, campaignId, cursorRef.current ?? undefined, HISTORY_PAGE)
      const incoming = res.logs || []
      // Dedup vs what's already shown (older + live) — the first page (no
      // cursor) returns the run tail, which overlaps the live WS feed.
      const seen = new Set<string>()
      for (const e of olderRef.current) seen.add(e.time + '|' + e.message)
      for (const e of logsRef.current) seen.add(e.time + '|' + e.message)
      const fresh = incoming.filter(e => !seen.has(e.time + '|' + e.message))
      if (fresh.length > 0) {
        const renderedAdded = fresh.filter(passesFilterRef.current).length
        setOlder(prev => [...fresh, ...prev])
        if (renderedAdded > 0) setFirstItemIndex(i => i - renderedAdded)
      }
      // Advance cursor even if all deduped, so the next page is strictly older.
      if (res.oldest_id != null) cursorRef.current = res.oldest_id
      const more = !!res.has_more && incoming.length > 0
      hasMoreRef.current = more
      setHasMore(more)
    } catch {
      /* transient — operator can retry by scrolling up / clicking again */
    } finally {
      loadingOlderRef.current = false
      setLoadingOlder(false)
    }
  }, [campaignId, mode])

  // Reset history when switching projects (older lines belong to one cid).
  useEffect(() => {
    setOlder([]); olderRef.current = []
    cursorRef.current = null
    const enable = !!campaignId
    hasMoreRef.current = enable
    setHasMore(enable)
    setFirstItemIndex(VIRTUOSO_START)
    loadingOlderRef.current = false
    setLoadingOlder(false)
    // Gate auto-load briefly so Virtuoso's initial top-reach (before the
    // first scroll-to-bottom) doesn't fire a spurious history fetch.
    readyRef.current = false
    const t = setTimeout(() => { readyRef.current = true }, 600)
    return () => clearTimeout(t)
  }, [campaignId])

  const handleStartReached = useCallback(() => {
    if (!readyRef.current) return
    void loadOlder()
  }, [loadOlder])

  const handleScrollerRef = useCallback((el: HTMLElement | Window | null) => {
    if (!el || el instanceof Window) return
    const htmlEl = el as HTMLElement
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = htmlEl
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      const atBottom = distanceFromBottom <= STICK_THRESHOLD
      // Вверх двигаемся только при РУЧНОМ скролле — программный авто-скролл
      // вниз увеличивает scrollTop, поэтому он сюда не попадает.
      const goingUp = scrollTop < lastScrollTopRef.current - 4
      lastScrollTopRef.current = scrollTop
      if (atBottom) {
        // У нижнего края (в т.ч. вернулись сами) → включаем прилипание.
        if (!autoRef.current) {
          setAutoScrollSync(true)
          setNewCount(0)
        }
      } else if (goingUp && autoRef.current) {
        // Оператор отошёл вверх от низа → выключаем прилипание, не дёргаем.
        setAutoScrollSync(false)
      }
    }
    htmlEl.addEventListener('scroll', onScroll, { passive: true })
  }, [setAutoScrollSync])

  // Backstop: Virtuoso тоже сообщает о достижении низа (его детекция учитывает
  // догрузку/ресайз). Никогда не ВЫКЛЮЧАЕТ прилипание — только включает —
  // чтобы не конфликтовать с onScroll и не ломать первичный автоскролл.
  const handleBottomState = useCallback((atBottom: boolean) => {
    if (atBottom && !autoRef.current) {
      setAutoScrollSync(true)
      setNewCount(0)
    }
  }, [setAutoScrollSync])

  const wsLabel = WS_LABELS[String(wsState)] || { text: '', cls: '' }

  const levelCounts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const l of logs) c[l.level] = (c[l.level] || 0) + 1
    return c
  }, [logs])

  const renderRow = useCallback((_index: number, item: EnrichedEntry) => {
    const { entry, sem, parsed } = item
    const colors = SEM_COLORS[sem]
    let msg = parsed ? parsed.rest : entry.message
    // Strip the invisible colour marker (⟦c:NAME⟧) — it only drives colour.
    msg = msg.replace(COLOR_MARKER_RE_G, '')
    msg = stripPowDetails(msg)
    const { project, body } = splitProjectPrefix(msg)
    msg = body
    if (isMobile) msg = shortenForMobile(msg)
    const tag = parsed?.tag
    let badge = SEM_BADGES[sem]
    let actionNum = ''

    if (sem === 'action') {
      const m = msg.match(/\[#(\d{2})\]\s*/)
      if (m) {
        actionNum = m[1]
        msg = msg.slice(m[0].length)
      }
    }

    const bold = sem === 'success' || sem === 'critical' || sem === 'found' || sem === 'action'

    if (isMobile) {
      return (
        <div className={`${colors.text} ${colors.bg} border-l-2 ${colors.border} pl-2 pr-1 py-1 hover:brightness-95 dark:hover:brightness-125 transition-all min-w-0 max-w-full overflow-hidden rounded-r-sm my-0.5 ${bold ? 'font-medium' : ''}`}>
          {/* Line 1: time + tag + badge + project */}
          <div className="flex items-center gap-1.5 mb-0.5 min-w-0 max-w-full">
            <span className="tabular-nums text-[10px] text-muted-foreground/60 shrink-0">
              {fmtTime(entry.time)}
            </span>
            {tag && (
              <span className="text-[10px] font-semibold text-muted-foreground shrink-0">
                {tag}
              </span>
            )}
            {actionNum ? (
              <span className={`${colors.badge || ''} ${colors.badgeBg || ''} text-[9px] font-bold px-1 py-[1px] rounded text-white tabular-nums leading-tight shrink-0`}>
                #{actionNum}
              </span>
            ) : badge ? (
              <span className={`${colors.badge || ''} ${colors.badgeBg || ''} text-[8px] font-bold px-1 py-[0px] rounded leading-tight shrink-0`}>
                {badge}
              </span>
            ) : null}
            {project && (
              <span className="text-[10px] text-muted-foreground/80 truncate min-w-0 flex-1" title={project}>
                [{project}]
              </span>
            )}
          </div>
          {/* Line 2: message */}
          <div className="text-[11px] leading-snug min-w-0 max-w-full"
            style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: msg.includes('\n') ? 'pre-wrap' : 'normal' }}>
            {renderMessageWithUrls(msg, 'text-[9px] opacity-80')}
          </div>
        </div>
      )
    }

    return (
      <div className={`${colors.text} ${colors.bg} border-l-2 ${colors.border} pl-2 pr-1 py-[1px] hover:brightness-95 dark:hover:brightness-125 transition-all flex min-w-0 items-baseline gap-0 rounded-r-sm my-[1px] ${bold ? 'font-medium' : ''}`}>
        <span className="mr-1.5 select-none shrink-0 tabular-nums text-xs text-muted-foreground/50">
          {fmtTime(entry.time)}
        </span>
        {tag && (
          <span className="mr-1 select-none shrink-0 text-xs font-semibold text-muted-foreground">
            {tag}
          </span>
        )}
        {actionNum ? (
          <span className={`${colors.badge || ''} ${colors.badgeBg || ''} mr-1.5 select-none shrink-0 text-[10px] font-bold px-1.5 py-[1px] rounded text-white tabular-nums leading-tight`}>
            #{actionNum}
          </span>
        ) : badge ? (
          <span className={`${colors.badge || ''} ${colors.badgeBg || ''} mr-1.5 select-none shrink-0 text-[9px] font-bold px-1 py-[0px] rounded leading-tight`}>
            {badge}
          </span>
        ) : null}
        {project && (
          <span className="mr-1 select-none shrink-0 text-xs text-muted-foreground/80">
            [{project}]
          </span>
        )}
        <span className={`min-w-0 flex-1 break-anywhere md:break-all${msg.includes('\n') ? ' whitespace-pre-wrap' : ''}`}>
          {renderMessageWithUrls(msg, 'text-[10px] opacity-80')}
        </span>
      </div>
    )
  }, [isMobile])

  const downloadLogs = useCallback(() => {
    // Idea #54: если знаем campaignId — забираем полный лог из БД
    // (включая обрезанный ring-buffer'ом fronend-хвост) одним кликом
    // через серверный endpoint. Иначе фолбэк на blob из буфера.
    if (campaignId) {
      const a = document.createElement('a')
      a.href = `/api/${mode}/campaigns/${encodeURIComponent(campaignId)}/logs/download`
      a.rel = 'noopener'
      a.click()
      return
    }
    const source = getAllLogs ? getAllLogs() : logs
    const lines = source.map(l => {
      const t = new Date(l.time * 1000).toLocaleTimeString('ru-RU', { hour12: false })
      const lv = l.level.padEnd(7)
      return `${t} [${lv}] ${l.message.replace(COLOR_MARKER_RE_G, '')}`
    })
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    a.download = `log_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }, [logs, getAllLogs, campaignId, mode])

  return (
    <div className="flex min-w-0 flex-col h-full">
      {/* ── Header ── */}
      <div className="flex min-w-0 items-center flex-nowrap px-2 md:px-2.5 min-h-9 py-1 md:py-0 md:h-9 shrink-0 gap-1.5 md:gap-2 border-b border-border bg-muted/30 overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]">
        <div className="flex items-center gap-2 shrink-0">
          {logs.length > 0 && (
            <span className="inline-flex items-center h-[22px] px-2 text-[11px] tabular-nums font-medium rounded-md bg-muted text-muted-foreground border border-border/50">
              {filtered.length}
              <span className="text-muted-foreground/40 mx-0.5">/</span>
              {logs.length}
            </span>
          )}
          {wsLabel.text && (
            <span className={`text-[11px] whitespace-nowrap ${wsLabel.cls}`}>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-current mr-1 align-middle animate-pulse" />
              {wsLabel.text}
            </span>
          )}
        </div>

        <div className="hidden min-w-0 md:block md:flex-1" />

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Worker filter — desktop only */}
          <div className="hidden md:flex items-center gap-1 shrink-0">
            <span className="text-[11px] text-muted-foreground/70 font-medium">W:</span>
            <Input
              type="text"
              inputMode="numeric"
              value={workerFilter}
              onChange={e => setWorkerFilter(e.target.value.replace(/[^0-9]/g, ''))}
              className="h-[22px] w-7 px-0.5 text-[11px] text-center tabular-nums rounded-md border-border/60"
              title="0 — все потоки, 1+ — конкретный"
            />
          </div>

          <div className="hidden md:block h-4 w-px bg-border/50 mx-0.5" />

          {/* Level filter buttons */}
          <div className="flex items-center rounded-lg bg-muted/80 p-[3px] gap-[2px] shrink-0 border border-border/40">
            {LEVELS.filter(lv => lv !== 'DEBUG' || showDebug).map(lv => {
              const cfg = LEVEL_BTN_CONFIG[lv]
              const active = activeLevels.has(lv)
              const count = levelCounts[lv] || 0
              return (
                <button
                  key={lv}
                  onClick={() => toggleLevel(lv)}
                  className={`shrink-0 h-[22px] px-1.5 md:px-2 rounded-md text-[10px] md:text-[11px] font-medium transition-all border cursor-pointer whitespace-nowrap ${
                    active
                      ? `${cfg.activeColor} shadow-sm`
                      : 'text-muted-foreground/35 border-transparent hover:text-muted-foreground/60'
                  }`}
                  title={`${cfg.label}: ${count}`}
                >
                  {cfg.label}
                </button>
              )
            })}
          </div>

          <div className="hidden md:block h-4 w-px bg-border/50 mx-0.5" />

          {/* Search — desktop only */}
          <Input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск..."
            className="hidden md:block h-[22px] pl-2 pr-2 text-[11px] w-28 shrink-0 rounded-md border-border/60"
          />
        </div>

        <div className="hidden md:block h-4 w-px bg-border/50" />

        {/* Action buttons */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant={autoScroll ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => {
              const next = !autoScroll
              if (next) {
                // Включаем прилипание вручную → прыгаем вниз и сбрасываем бейдж.
                if (filtered.length > 0) {
                  virtuosoRef.current?.scrollToIndex({ index: filtered.length - 1, behavior: 'auto' })
                }
                setNewCount(0)
              }
              setAutoScrollSync(next)
            }}
            className="h-[22px] w-[22px] cursor-pointer rounded-md"
            title="Автопрокрутка"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={downloadLogs}
            className="h-[22px] w-[22px] text-muted-foreground cursor-pointer rounded-md hover:text-foreground"
            title={campaignId ? 'Скачать полный лог последнего запуска' : 'Скачать видимый буфер'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" x2="12" y1="15" y2="3" />
            </svg>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClear}
            className="h-[22px] w-[22px] text-muted-foreground cursor-pointer rounded-md hover:text-red-500"
            title="Очистить лог"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </Button>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 min-h-0 font-mono text-xs leading-[19px] select-text bg-background relative overflow-x-hidden">
        {filtered.length > 0 ? (
          <Virtuoso
            ref={virtuosoRef}
            data={filtered}
            itemContent={renderRow}
            // Prepend support: firstItemIndex decreases by how many older
            // lines we prepend (loadOlder), so the viewport stays anchored.
            firstItemIndex={firstItemIndex}
            // Scrolling to the very top pulls older history from logs.db.
            startReached={handleStartReached}
            // followOutput ВЫКЛЮЧЕН намеренно (см. useEffect-приклеивание выше).
            // Любое НЕ-false значение (даже функция, возвращающая false) держит
            // активным внутренний «size-increased» backstop Virtuoso, который
            // утаскивал оператора вниз при росте лога, игнорируя ручной скролл.
            followOutput={false}
            atBottomStateChange={handleBottomState}
            scrollerRef={handleScrollerRef}
            atBottomThreshold={STICK_THRESHOLD}
            overscan={100}
            className="h-full min-w-0 max-w-full overflow-x-hidden px-1 py-0.5"
          />
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <span>{logs.length === 0 ? 'Нет логов. Запустите воркеры.' : 'Нет совпадений по фильтру.'}</span>
            {campaignId && hasMore && (
              <button
                onClick={() => void loadOlder()}
                disabled={loadingOlder}
                className="h-7 px-3 rounded-full bg-muted text-foreground border border-border/60 hover:bg-muted/70 transition-all text-xs font-medium cursor-pointer disabled:opacity-50"
              >
                {loadingOlder ? 'Загрузка истории…' : 'Показать историю запуска'}
              </button>
            )}
          </div>
        )}
        {/* Scrolled up while history is available → offer to load older. */}
        {!autoScroll && filtered.length > 0 && campaignId && (hasMore || loadingOlder) && (
          <button
            onClick={() => void loadOlder()}
            disabled={loadingOlder}
            className="absolute top-2 left-1/2 -translate-x-1/2 h-7 px-3 rounded-full bg-card/95 text-foreground border border-border shadow-md hover:bg-muted transition-all text-[11px] font-medium flex items-center gap-1.5 cursor-pointer disabled:opacity-60 animate-in fade-in slide-in-from-top-1 duration-200 z-10"
            title="Подгрузить более ранние логи из истории"
          >
            {loadingOlder ? (
              <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m18 15-6-6-6 6" />
              </svg>
            )}
            {loadingOlder ? 'Загрузка…' : 'Показать более ранние'}
          </button>
        )}
        {!autoScroll && filtered.length > 0 && (
          <button
            onClick={() => {
              virtuosoRef.current?.scrollToIndex({ index: filtered.length - 1, behavior: 'smooth' })
              setAutoScrollSync(true)
              setNewCount(0)
            }}
            className="absolute bottom-3 right-4 h-8 px-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all text-xs font-medium flex items-center gap-1.5 cursor-pointer animate-in fade-in slide-in-from-bottom-2 duration-200"
            title="К последним логам"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
            {newCount > 0 ? `${newCount} новых` : 'Вниз'}
          </button>
        )}
      </div>
    </div>
  )
}
